/**
 * Comment threads and field requests (ADR-0102 addendum 2): body, replies,
 * reply box, close / reopen, and for field requests the value change since
 * the request plus "Gå till fältet". Bodies are contract content: display
 * only.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { tenantApi, type ReviewThread } from '../../api.js';
import { useTenantQuery } from '../../query.js';
import { valueChangedText } from './review-model.js';

export function useUserNames(): ReadonlyMap<string, string> {
  const usersQ = useTenantQuery('users.list', () => tenantApi.users.list(), [], {
    ignoreActiveGroup: true,
    revalidateOnFocus: false,
  });
  return useMemo(() => new Map((usersQ.data?.users ?? []).map((u) => [u.id, u.name ?? u.email])), [usersQ.data]);
}

export function formatCommentAt(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' });
}

const errorText = (e: unknown): string => {
  const m = (e as Error)?.message ?? '';
  if (m === 'contract_not_commentable') return 'Avtalet kan inte kommenteras i sin nuvarande status.';
  return 'Kommentaren kunde inte sparas.';
};

/** A textarea + send; resolves `true` when sent. */
export function CommentComposer({
  placeholder = 'Skriv en kommentar…',
  submitLabel = 'Skicka',
  autoFocus,
  onSubmit,
  onCancel,
}: {
  placeholder?: string;
  submitLabel?: string;
  autoFocus?: boolean;
  onSubmit: (body: string) => Promise<void>;
  onCancel?: () => void;
}) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    setErr(null);
    try {
      await onSubmit(text);
      setBody('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="flex flex-col gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      <textarea
        autoFocus={autoFocus}
        aria-label={placeholder}
        placeholder={placeholder}
        rows={2}
        maxLength={4000}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel?.();
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send();
        }}
        className="w-full resize-y rounded border border-stone-300 px-2 py-1 text-xs"
      />
      {err && <p className="text-[11px] text-red-700">{err}</p>}
      <div className="flex justify-end gap-2 text-xs">
        {onCancel && (
          <button type="button" className="underline" onClick={onCancel}>
            Avbryt
          </button>
        )}
        <button
          type="submit"
          disabled={busy || !body.trim()}
          className="rounded bg-stone-900 px-2.5 py-1 font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Skickar…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

export function ReviewThreadCard({
  thread,
  contractId,
  names,
  writable,
  heading,
  onGoToField,
  onShow,
  onChanged,
}: {
  thread: ReviewThread;
  contractId: string;
  names: ReadonlyMap<string, string>;
  writable: boolean;
  /** What the thread is about (field label, "Förslag #4", …). */
  heading?: ReactNode;
  onGoToField?: () => void;
  onShow?: () => void;
  onChanged: () => Promise<void> | void;
}) {
  const [replying, setReplying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const changed = valueChangedText(thread);
  const who = (id: string) => names.get(id) ?? 'Okänd användare';

  const toggle = async () => {
    setBusy(true);
    setErr(null);
    try {
      const input = { contract_id: contractId, thread_id: thread.id };
      if (thread.resolved) await tenantApi.review_comments.reopen(input);
      else await tenantApi.review_comments.resolve(input);
      await onChanged();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      className={
        'rounded border p-2 text-xs ' +
        (thread.resolved ? 'border-stone-100 bg-stone-50 opacity-75' : thread.kind === 'field_request' ? 'border-amber-200 bg-amber-50/40' : 'border-stone-200 bg-white')
      }
    >
      <div className="flex items-baseline gap-1.5">
        {heading && <span className="min-w-0 flex-1 truncate font-medium text-stone-800">{heading}</span>}
        <span className={'shrink-0 rounded border px-1 py-px text-[10px] ' + (thread.resolved ? 'border-stone-200 text-stone-500' : 'border-amber-300 text-amber-800')}>
          {thread.resolved ? 'Stängd' : thread.kind === 'field_request' ? 'Begäran' : 'Öppen'}
        </span>
      </div>
      <p className="mt-1 whitespace-pre-wrap text-stone-800">{thread.body}</p>
      <div className="mt-0.5 text-[11px] text-stone-500">
        {who(thread.author_user_id)} · {formatCommentAt(thread.created_at)}
      </div>
      {changed && <p className="mt-1 rounded bg-white px-1.5 py-1 text-[11px] text-stone-700 ring-1 ring-amber-200">{changed}</p>}
      {thread.replies.length > 0 && (
        <ol className="mt-2 flex flex-col gap-1.5 border-l-2 border-stone-200 pl-2">
          {thread.replies.map((r) => (
            <li key={r.id}>
              <p className="whitespace-pre-wrap text-stone-800">{r.body}</p>
              <div className="text-[11px] text-stone-500">
                {who(r.author_user_id)} · {formatCommentAt(r.created_at)}
              </div>
            </li>
          ))}
        </ol>
      )}
      {err && <p className="mt-1 text-[11px] text-red-700">{err}</p>}
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
        {onGoToField && (
          <button type="button" className="font-medium underline" onClick={onGoToField}>
            Gå till fältet
          </button>
        )}
        {onShow && (
          <button type="button" className="underline" onClick={onShow}>
            Visa
          </button>
        )}
        {writable && !thread.resolved && !replying && (
          <button type="button" className="underline" onClick={() => setReplying(true)}>
            Svara
          </button>
        )}
        {writable && (
          <button type="button" className="underline disabled:opacity-50" disabled={busy} onClick={() => void toggle()}>
            {thread.resolved ? 'Öppna igen' : 'Stäng'}
          </button>
        )}
      </div>
      {replying && (
        <div className="mt-2">
          <CommentComposer
            autoFocus
            placeholder="Skriv ett svar…"
            submitLabel="Svara"
            onCancel={() => setReplying(false)}
            onSubmit={async (body) => {
              await tenantApi.review_comments.reply({ contract_id: contractId, thread_id: thread.id, body });
              setReplying(false);
              await onChanged();
            }}
          />
        </div>
      )}
    </li>
  );
}

/** A comment on a text change: anchored to the change's log entry (ADR-0102 addendum 2). */
export async function createActionComment(contractId: string, attachmentId: string | null, seq: number, body: string): Promise<void> {
  await tenantApi.review_comments.create({
    contract_id: contractId,
    attachment_id: attachmentId,
    anchor_kind: 'action',
    anchor: seq,
    kind: 'comment',
    body,
  });
}

/**
 * The threads of one change (adaptation group or suggestion) and, when
 * open, the composer for a new comment on it. Shared by the change list
 * and the suggestion list.
 */
export function ChangeThreads({
  contractId,
  attachmentId,
  threads,
  names,
  writable,
  composeSeq,
  onCloseComposer,
  onShowNode,
  onChanged,
}: {
  contractId: string;
  attachmentId: string | null;
  threads: readonly ReviewThread[];
  names: ReadonlyMap<string, string>;
  writable: boolean;
  /** The seq a new comment anchors to; `null` = composer closed. */
  composeSeq: number | null;
  onCloseComposer: () => void;
  onShowNode?: (address: string) => void;
  onChanged: () => Promise<void> | void;
}) {
  return (
    <>
      {threads.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1.5">
          {threads.map((t) => (
            <ReviewThreadCard
              key={t.id}
              thread={t}
              contractId={contractId}
              names={names}
              writable={writable}
              heading={t.anchor_kind === 'node' ? 'Kommentar på stycket' : undefined}
              onShow={t.anchor_kind === 'node' && t.anchor && onShowNode ? () => onShowNode(t.anchor!) : undefined}
              onChanged={onChanged}
            />
          ))}
        </ul>
      )}
      {composeSeq != null && (
        <div className="mt-2">
          <CommentComposer
            autoFocus
            placeholder="Kommentera ändringen…"
            onCancel={onCloseComposer}
            onSubmit={async (body) => {
              await createActionComment(contractId, attachmentId, composeSeq, body);
              onCloseComposer();
              await onChanged();
            }}
          />
        </div>
      )}
    </>
  );
}
